import { BucketRange } from '../_types';

/**
 * Normalizes a list of bucket ranges into a canonical form.
 *
 * Each range is sanitized by:
 * - Clamping `from` and `to` to [0, 99]
 * - Ensuring `from <= to` (swaps if needed)
 *
 * The resulting ranges are then sorted by `from` and merged so that
 * overlapping or contiguous ranges are combined into a single range.
 *
 * @example
 * normalizeRanges([{ from: 5, to: 2 }, { from: 3, to: 7 }])
 * @returns
 * [{ from: 2, to: 7 }]
 */
export function normalizeRanges(ranges: BucketRange[]): BucketRange[] {
  const sanitized = ranges
    .map(({ from, to }) => ({
      from: Math.max(0, Math.min(99, from)),
      to: Math.max(0, Math.min(99, to)),
    }))
    .map(({ from, to }) => ({ from: Math.min(from, to), to: Math.max(from, to) }))
    .sort((a, b) => a.from - b.from);

  if (sanitized.length === 0) return [];

  const merged: BucketRange[] = [sanitized[0]];
  for (let i = 1; i < sanitized.length; i++) {
    const current = sanitized[i];
    const lastMerged = merged[merged.length - 1];
    if (current.from <= lastMerged.to + 1) {
      lastMerged.to = Math.max(lastMerged.to, current.to);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}
